import { TID } from '@/lib/testIds';
import { ArrowUpRight } from 'lucide-react';

const services = [
  {
    key: 'strategy',
    number: '01',
    title: 'Business Strategy',
    tag: 'Models · Growth · Positioning',
    items: [
      'Business Models',
      'Growth Strategy',
      'Market Positioning',
      'Business Planning',
      'Expansion Strategy',
      'Competitive Advantage',
    ],
  },
  {
    key: 'operations',
    number: '02',
    title: 'Operations Excellence',
    tag: 'Processes · SOPs · Governance',
    items: [
      'Business Process Design',
      'SOP Development',
      'Project Management Office',
      'Operational Efficiency',
      'Governance',
      'Performance Systems',
    ],
  },
  {
    key: 'organization',
    number: '03',
    title: 'Organization Development',
    tag: 'Structure · Culture · Leadership',
    items: [
      'Organization Structure',
      'Human Resources',
      'Culture Transformation',
      'Leadership Development',
      'Performance Management',
      'Capability Building',
    ],
  },
  {
    key: 'digital',
    number: '04',
    title: 'Digital Transformation',
    tag: 'AI · Automation · Intelligence',
    items: [
      'AI Adoption',
      'Automation',
      'Business Intelligence',
      'CRM Strategy',
      'Technology Roadmaps',
      'Digital Operating Models',
    ],
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid={TID.services.root}
      className="py-24 md:py-32 border-t border-white/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <p className="eyebrow mb-6">Services</p>
            <h2
              className="font-display font-semibold text-text tracking-tight leading-[1.02]"
              style={{ fontSize: 'clamp(38px, 5.6vw, 88px)' }}
            >
              What I <span className="text-ember">do.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="max-w-md text-[15px] leading-relaxed text-text/75">
              Four disciplines woven into a single operating system. Every
              engagement blends strategy with implementation — because
              recommendations that can&apos;t be executed aren&apos;t worth
              writing.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {services.map((s) => (
            <ServiceCard key={s.key} service={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  return (
    <div
      data-testid={TID.services.item(service.key)}
      className="group relative rounded-3xl bg-ink-2 border border-white/8 p-6 md:p-8 hover:border-ember/50 transition-colors duration-500 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 ember-glow opacity-0 group-hover:opacity-80 transition-opacity duration-700"
      />
      <div className="relative flex items-start justify-between gap-4">
        <span className="font-display font-semibold text-ember text-lg">
          {service.number}
        </span>
        <ArrowUpRight
          size={18}
          className="text-text/40 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-ember"
        />
      </div>

      <h3
        className="relative mt-6 font-display font-semibold text-text tracking-tight leading-[1.05]"
        style={{ fontSize: 'clamp(26px, 3vw, 40px)' }}
      >
        {service.title}
      </h3>
      <p className="relative mt-2 text-[11px] uppercase tracking-[0.22em] text-text-70">
        {service.tag}
      </p>

      <div className="relative mt-6 grid grid-cols-2 gap-x-4 gap-y-2">
        {service.items.map((i) => (
          <div
            key={i}
            className="flex items-start gap-2 text-[13px] text-text/80"
          >
            <span className="mt-1.5 h-1 w-1 rounded-full bg-ember shrink-0" />
            {i}
          </div>
        ))}
      </div>
    </div>
  );
}
